import { usePostHog } from 'posthog-js/react';

/**
 * Custom hook to check if a PostHog feature flag is enabled
 * @param flagName - The name of the feature flag to check
 * @param defaultValue - Default value if PostHog is not available or flag is undefined (defaults to false)
 * @returns boolean indicating if the feature flag is enabled
 */
export const useFeatureFlag = (flagName: string, defaultValue: boolean = false): boolean => {
  const posthog = usePostHog();

  if (!posthog) {
    return defaultValue;
  }

  try {
    return posthog.isFeatureEnabled(flagName) ?? defaultValue;
  } catch (error) {
    console.warn(`Error checking feature flag ${flagName}:`, error);
    return defaultValue;
  }
};

/**
 * Custom hook to get a PostHog feature flag variant value
 * @param flagName - The name of the feature flag to check
 * @param defaultValue - Default variant value if PostHog is not available or flag is undefined
 * @returns string variant value or default value
 */
export const useFeatureFlagVariant = (flagName: string, defaultValue: string = ''): string => {
  const posthog = usePostHog();

  if (!posthog) {
    return defaultValue;
  }

  try {
    const variant = posthog.getFeatureFlag(flagName);
    // getFeatureFlag returns the variant string if it's a multivariate flag, or true/false if it's a boolean flag
    // If it's a boolean flag that's enabled, it returns true, so we need to handle that
    if (variant === true) {
      return defaultValue; // Boolean flag enabled but no variant, use default
    }
    if (variant === false) {
      return defaultValue; // Boolean flag disabled, use default
    }
    // If it's a string variant, return it
    if (typeof variant === 'string') {
      return variant;
    }
    return defaultValue;
  } catch (error) {
    console.warn(`Error checking feature flag variant ${flagName}:`, error);
    return defaultValue;
  }
};

